import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { fromJS, List, Map } from 'immutable';
import { isString } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { IConfirmDialogResult } from '../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogService } from '../dialog/dialog.service';
import { BannerCanvasTypesEnum } from '../features-data/features-data.data';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { NavigationService } from '../navigation/navigation.service';
import { IIcon } from '../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../shared/components/icon/icons-sizes.enum';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../tracking/tracking.data';
import { MessageDeliveryBuilder } from './message-delivery.builder';
import { IMessageDeliveryData } from './message-delivery.interface';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { BILLING_MASTER_ROUTE } from '../main/main.data';
import { IFormFieldErrorMessages } from 'i2g-ng-ui';
import { doesStringContainHtml, doesStringContainLink } from 'i2g-yakka';
import { AttachmentView } from '../attachments/attachment-view.model';
import { IDocumentDataDispatcher } from '../document-data/document-data-dispatcher.interface';
import { Attachment } from '../attachments/attachment.model';

@Component({
  selector: 'app-message-delivery',
  templateUrl: 'message-delivery.component.html',
  styleUrls: ['message-delivery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [MessageDeliveryBuilder],
})
export class MessageDeliveryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() attachmentName = '';
  @Input() attachments: List<AttachmentView>;
  @Input() fileAttachments: List<Attachment>;
  @Input() attachmentSize: number;
  @Input() defaultBCC: string;
  @Input() defaultCC: string;
  @Input() defaultMessage: string;
  @Input() defaultSubject: string;
  @Input() defaultTo: string;
  @Input() isVerifiedAccount: boolean;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() featureToggles: Toggles;
  @Input() isDocument: true;
  @Input() documentDataDispatcher: IDocumentDataDispatcher;

  /**
   * If enabled, it will enable `to`, `cc` and `bcc` fields
   */
  @Input() enableRecipientFields = true;

  @Output()
  onMessageUpdated: EventEmitter<IMessageDeliveryData> = new EventEmitter<IMessageDeliveryData>();
  @Output()
  onStatusChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  attachmentIcon: IIcon;
  form: FormGroup;
  formKeys: any;
  maxRecipients$: Observable<Map<string, number>>;
  showBCC: boolean;
  showCC: boolean;
  showFooter: boolean;
  featureHighlightIcon: IIcon;
  ccButtonTapped: ITrackingAction;
  bccButtonTapped: ITrackingAction;
  disableEmailCustomization: boolean;
  disableCustomizeEmailDialogId: string;
  noHtmlAndLinksErrorMessage: IFormFieldErrorMessages;
  isEmailHtmlEnabled: boolean;
  enableFileAttachment: boolean;

  constructor(
    private _messageDeliveryBuilder: MessageDeliveryBuilder,
    private _featuresDataStore: FeaturesDataStore,
    private _dialogService: DialogService,
    private _navigationService: NavigationService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.featureToggles && this.featureToggles) {
      this.showFooter = !this.featureToggles.isAllAccess(
        Toggles.KEYS.CUSTOM_BRANDING
      );
      this.disableEmailCustomization = !this.featureToggles.isAllAccess(
        Toggles.KEYS.EDIT_EMAIL
      );
      this.isEmailHtmlEnabled = this.featureToggles.isAllAccess(
        Toggles.KEYS.EMAIL_HTML
      );
    }

    if (!this.form && this.featureToggles) {
      const bcc = this.defaultBCC;
      const cc = this.defaultCC;
      const message = this.defaultMessage;
      const subject = this.defaultSubject;
      const to = this.defaultTo;
      const filename = this.attachmentName;

      const emailFields = [message, subject, filename, to, cc, bcc];
      const isEachEmailFieldValid = emailFields.every((field) =>
        isString(field)
      );

      if (isEachEmailFieldValid) {
        const emailFooterFeatureName = Toggles.KEYS.CUSTOM_BRANDING;

        this.formKeys = this._messageDeliveryBuilder.FORM_KEYS;
        this.form = this._messageDeliveryBuilder.buildForm(
          bcc,
          cc,
          message,
          subject,
          to,
          filename,
          this.showFooter,
          this.disableEmailCustomization
        );
        this.showBCC = !!this.form.get(this.formKeys.BCC).value;
        this.showCC = !!this.form.get(this.formKeys.CC).value;

        this.updateFormData(fromJS(this.form.value));

        const footerToggleControl = this.form.get(this.formKeys.INCLUDE_FOOTER);

        footerToggleControl.valueChanges
          .pipe(takeUntil(this._destroyed))
          .subscribe((value) => {
            this._dialogService.openGrowthDialog(
              BannerCanvasTypesEnum.feature_lock,
              emailFooterFeatureName,
              this.onGrowthCta.emit.bind(this.onGrowthCta)
            );

            footerToggleControl.setValue(!value, { emitEvent: false });
          });
      }
    } else if (this.form) {
      const messageControl = this.form.get(this.formKeys.MESSAGE);
      const subjectControl = this.form.get(this.formKeys.SUBJECT);

      if (changes.defaultMessage && !messageControl.dirty) {
        messageControl.setValue(this.defaultMessage);
      } else if (changes.defaultSubject && !subjectControl.dirty) {
        subjectControl.setValue(this.defaultSubject);
      }
    }
  }

  ngOnInit() {
    this.attachmentIcon = {
      name: 'pdf',
      size: IconSizesEnum.custom,
    };

    this.maxRecipients$ = this._featuresDataStore.features$.pipe(
      map((features) =>
        features.payloads.getIn(['email_recipients', 'limits'])
      ),
      shareReplay(1)
    );

    this.disableCustomizeEmailDialogId = v4();
    this.ccButtonTapped = this._trackingActionBuilder.buildButtonTapped(
      'cc',
      this.trackingScreenName
    );
    this.bccButtonTapped = this._trackingActionBuilder.buildButtonTapped(
      'bcc',
      this.trackingScreenName
    );

    this._featuresDataStore
      .hasFeatureHighlightCanvas(Toggles.KEYS.CUSTOM_BRANDING)
      .subscribe((hasFeatureHighlightCanvas: boolean) => {
        if (hasFeatureHighlightCanvas) {
          this.featureHighlightIcon = {
            name: 'premium-feature',
            size: IconSizesEnum.medium,
            customClass: 'premium-icon',
          };
        }
      });

    this.enableFileAttachment =
      this.featureToggles.isAllAccess(Toggles.KEYS.FILE_ATTACHMENT) &&
      this.isDocument;
  }

  toggleBCC() {
    this.showBCC = !this.showBCC;
  }

  toggleCC() {
    this.showCC = !this.showCC;
  }

  updateFormData(data: Map<string, any>) {
    const emailBody = this.form.get(this.formKeys.MESSAGE).value;

    if (
      !this.isEmailHtmlEnabled &&
      (doesStringContainHtml(emailBody) || doesStringContainLink(emailBody))
    ) {
      this.form.get(this.formKeys.MESSAGE).setErrors({ generic: true });
      this.noHtmlAndLinksErrorMessage = {
        generic: {
          message: 'VERIFY_ACCOUNT.NO_EMBED_HTML_LINKS',
        },
      };
    }

    const messageDeliveryData: IMessageDeliveryData = this._messageDeliveryBuilder.buildMessageDeliveryData(
      fromJS(this.form.value),
      this.form.valid,
      this.disableEmailCustomization,
      this.defaultMessage,
      this.defaultSubject
    );

    this.onMessageUpdated.emit(messageDeliveryData);
  }

  showFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      Toggles.KEYS.CUSTOM_BRANDING,
      this.onGrowthCta.emit.bind(this.onGrowthCta)
    );
  }

  showEmailCustomizationDialog() {
    this._dialogService.openConfirmDialog(this.disableCustomizeEmailDialogId);
  }

  onDisableEmailCustomizationDialogClose({ result }: IConfirmDialogResult) {
    if (result) {
      this._navigationService.goToByUrl(BILLING_MASTER_ROUTE);
    }
  }

  ngOnDestroy() {
    const emailFooterFeatureName = Toggles.KEYS.CUSTOM_BRANDING;

    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      emailFooterFeatureName
    );
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      emailFooterFeatureName
    );
    this._dialogService.closeConfirmDialog(this.disableCustomizeEmailDialogId);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
